// @ts-strict-ignore
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base';
import { APIResponse, GenericAPIResponse, Project } from '../definitions';
import { Run } from '../components/run-management/definitions';
import { DateParts } from './date-helper';
@Injectable()
export class RunService extends BaseService {
  constructor(private http: HttpClient, @Inject('project') public project: Project) {
    super(project);
  }

  public addRunFrom(newRunType: string, run: Run) {
    return this.http.post<GenericAPIResponse<any>>(`${this.baseUrl}/runs/add-from`, {
      runId: run.runId,
      type: newRunType,
    });
  }

  getRun(runId: number) {
    return this.http.get(`${this.baseUrl}/runs/single/${runId}`);
  }

  getSchedules() {
    return this.http.get<GenericAPIResponse<RunSchedule[]>>(`${this.baseUrl}/runs/schedules`);
  }

  getAvaliableSchedules(runItemId: number, runId: number) {
    const params = new HttpParams().set('runId', runId).set('runItemId', runItemId);
    return this.http.get(`${this.baseUrl}/runs/available-schedules`, { params });
  }

  validateDraftBalances(runItemIds: number[], mode: string): Observable<any> {
    const data = {
      runItemIds,
      mode,
    };
    return this.http.post(
      `${this.baseUrl}/runs/validate-draft-balances`,
      data,
      this.requestOptions,
    );
  }

  getSelectedSchedules() {
    return this.http.get(`${this.baseUrl}/runs/selected-schedules`);
  }

  getStatuses() {
    return this.http.get<APIResponse>(`${this.baseUrl}/runs/statuses`);
  }

  public getRuns(initial: string | number) {
    return this.http.get(`${this.baseUrl}/runs?initial=` + initial);
  }

  public getAll(params?: { limit: number; page: number; searchParams: object }) {
    return this.http.post<GenericAPIResponse<any>>(
      `${this.baseUrl}/runs/run-search`,
      params,
      this.requestOptions,
    );
  }

  getRunNumbers() {
    return this.http.get(`${this.baseUrl}/runs/numbers`);
  }

  getFunders() {
    return this.http.get(`${this.baseUrl}/runs/get-funders`);
  }

  deleteRunItem(runItemIds) {
    let data = {
      runItemIds,
    };
    return this.http.post<APIResponse>(
      `${this.baseUrl}/runs/delete-run-items`,
      data,
      this.requestOptions,
    );
  }

  getExportData(params?: any): Observable<any> {
    params.page = 1;
    return this.http.post(`${this.baseUrl}/runs/run-search`, params, this.requestOptions);
  }

  finaliseRun(params?: any) {
    const apiEndpoint = `${this.baseUrl}/runs/finalise`;
    return this.http.post<APIResponse>(apiEndpoint, params, this.requestOptions);
  }

  storeRun(runNotes, runDate: string) {
    const apiEndpoint = `${this.baseUrl}/runs/store`;

    const data = {
      runDate,
      runNotes,
    };

    return this.http.post(apiEndpoint, data, this.requestOptions);
  }

  updateRun(
    runId: number,
    runItemId: number,
    runDate: string,
    scheduleId: number,
    runNotes: string,
    scheduleType: number,
    bankRef: number,
    irdConf: string,
    repaymentDate?: string,
  ): Observable<any> {
    const apiEndpoint = `${this.baseUrl}/runs/update`;
    let data = {
      runId,
      runItemId,
      runDate,
      scheduleId,
      runNotes,
      scheduleType,
      bankRef,
      irdConf,
      repaymentDate,
    };

    return this.http.post(apiEndpoint, data, this.requestOptions);
  }

  updateNotes(runId: string, notes: string) {
    const apiEndpoint = `${this.baseUrl}/runs/${runId}/notes`;

    const data = {
      notes,
    };

    return this.http.put(apiEndpoint, data);
  }

  updateRunItemStatus(runItemId: number, runStatusTag: string) {
    const apiEndpoint = `${this.baseUrl}/runs/item/${runItemId}/status`;

    const data = {
      runStatusTag,
    };

    return this.http.post(apiEndpoint, data);
  }

  updateMultipleRunItemStatus(runItemStatus: any[]) {
    const apiEndpoint = `${this.baseUrl}/runs/status/multiple`;

    const data = {
      runItemStatus,
    };

    return this.http.post<APIResponse>(apiEndpoint, data);
  }

  convertFromDraftValidation(runItemIds: number[]) {
    const apiEndpoint = `${this.baseUrl}/runs/status/convert-from-draft`;
    return this.http.post<APIResponse>(apiEndpoint, { runItemIds });
  }

  associateRun(
    runId: number,
    runScheduleId: number,
    runNotes: string,
    bankAcc: string,
    transactionItems: number[],
    runDate: string,
    runIndex: number,
  ) {
    const apiEndpoint = `${this.baseUrl}/runs/associate`;

    const data = {
      runScheduleId,
      runNotes,
      bankAcc,
      transactionItems,
      runDate,
      runId,
      runIndex,
    };

    return this.http.post<GenericAPIResponse<number>>(apiEndpoint, data, this.requestOptions);
  }

  unassociateRun(runScheduleId, transactionItems) {
    const apiEndpoint = `${this.baseUrl}/runs/unassociate`;
    const data = {
      runScheduleId,
      transactionItems,
    };

    return this.http.post(apiEndpoint, data, this.requestOptions);
  }

  public performRunCheck(runDate: string, transactionItems: number[], runNumber: number | string) {
    const apiEndpoint = `${this.baseUrl}/runs/check`;
    const createRun = runNumber === 'create';
    const data = { runDate, transactionItems, runNumber, createRun };

    return this.http.post<GenericAPIResponse<RunCheckResults>>(
      apiEndpoint,
      data,
      this.requestOptions,
    );
  }

  getViewRunData(runId: number, runItemId?: number): Observable<any> {
    let url = `${this.baseUrl}/runs/${runId}`;
    if (runItemId) {
      url += `?runItemId=${runItemId}`;
    }
    return this.http.get(url);
  }

  doFilterLookup(term: string, tportal: boolean, searchUnallocated: boolean) {
    const url = `${this.baseUrl}/runs/filter?term=${term}&tportal=${tportal}&searchUnallocated=${
      searchUnallocated ? 1 : 0
    }`;
    return this.http.get(url);
  }

  public validateRun(runItems: RunItem[]) {
    const data = {
      runItems,
    };
    return this.http.post<APIResponse>(`${this.baseUrl}/runs/validate`, data);
  }

  public runAllocateItem(funderAllocationParams: any): Observable<any> {
    const params = new HttpParams({ fromObject: funderAllocationParams });
    const url = `${this.baseUrl}/runs/run-allocate-item`;
    return this.http.get(url, { params });
  }

  public formatDateForAllocation(date: DateParts): string {
    return date['year'] > 0
      ? `${date['year']}${this.padNum(date['month'])}${this.padNum(date['day'])}`
      : '';
  }

  private padNum(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  public getFormattedDate(date: string): string {
    const dateParts = date.split(' ')[0].split('-');
    return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
  }
}

export interface RunNumber {
  runId: number;
  runDate: string;
  notes: string;
  runScheduleId: number;
  runIndex: number;
  initial: string;
  runItemId: number;
  itemNotes: string;
  bankAccountRef: number;
  runIdentifier: number;
  rnk: number;
}

export interface RunSchedule {
  bankAccountRef: number;
  initial: string;
  productId: number;
  runScheduleId: number;
  schedule: string;
}

export interface RunRequest {
  schedule: number;
  runNumber: string;
  bankAcc: number;
  runNotes: string;
  runDateString: string;
  runIndex?: number;
}

export type RunItem = {
  amount: number;
  bankRef: string;
  dateAuthorised: string;
  dateComplete: string;
  runId: number;
  runItemId: number;
  runNumber: string;
  scheduleStatus: string;
  scheduleStatusTag: string;
  scheduleType: string;
  actionInfo?: any;
};

export type ColumnTotals = {
  deposit: number;
  flexitaxFinance: number;
  transfers: number;
  salesRefunds: number;
  estTmnzMargin: number;
  unallocatedDeposit: number;
  unallocatedFlexitaxFinance: number;
  unallocatedSalesRefunds: number;
  unallocatedTransfers: number;
};

export type RunNumberSearchFilterData = {
  runNumber: string;
  runItemId: number;
};

export type RunNumberSuggestion = {
  runIdentifier: string;
  runId: number;
  runItemId: number;
};

export type RunCheckResult = {
  REASSESSMENTDATE: Date;
  TTD: Date;
  clientName: string;
  depositDate: Date;
  irdNumber: string;
  runDate: string;
  transactionDate: Date;
  transactionId: number;
  transactionItemId: number;
  warningMessage: string;
};

export type RunCheckResults = {
  warningCount: number;
  warnings: RunCheckResult[];
};
